import ProviderAPIClient, { type TrfPdf } from '@/features/auth/ProviderAPIClient'

/**
 * Fetch a placed order's server-rendered TRF PDF (GEN-4161).
 *
 * provider-api renders the TRF at submit and keeps it in S3, then streams the bytes back to the
 * SPA same-origin (JWT-authenticated) — never a pre-signed S3 URL the browser must fetch
 * cross-origin. Fetching S3 directly is blocked inside the Epic SMART embedded iframe, so
 * streaming through the API is what makes a returning user re-view the exact document that was
 * submitted. The bytes are handed to the existing `PDFViewer`.
 *
 * @param orderId Placed order id (also the S3 key prefix).
 * @param caller  Audit origin for the order-details read.
 * @param signal  Abort signal so an in-flight fetch is cancelled on unmount / re-fetch.
 * @returns The PDF bytes and filename, or `null` when the order has no TRF artifact at all —
 *   an expected state for orders placed before server-side generation shipped
 *   whose canonical document can no longer be reconstructed. Callers treat that
 *   as "fall back to client-side generation", not as a failure.
 */
export async function fetchOrderTrfPdfBytes(
  orderId: string,
  caller: string,
  signal?: AbortSignal
): Promise<TrfPdf | null> {
  const client = ProviderAPIClient.getInstance()
  return client.getOrderTrfPdfContent(orderId, caller, signal)
}
